import { Paper, TextField } from "@mui/material";
import { arrayMoveImmutable } from "array-move";
import React, { useEffect, useState } from "react";
import SortableList, { SortableItem, SortableKnob } from "react-easy-sort";
import { BsPencilFill } from "react-icons/bs";
import { RiDraggable } from "react-icons/ri";
import { toast } from "react-toastify";
import { debounce, updateDisplayOrder } from "../../../common/utils";
import { updateFormDropdownBulk } from "../../../services/formDropdownsApiService";
import FormDropdownsCreateDialog from "./FormDropdownsCreateDialog";
import { IFormDropdown } from "./types";

interface IProps {
  formDropdown: any[];
  onListUpdated: (formDropdown: any[]) => void;
}

const sendRequest = async (data: IFormDropdown[]): Promise<any> => {
  try {
    const resp = await updateFormDropdownBulk(data);
    toast.success("FormDropdown sorted");
  } catch (error) {
    console.log("sendRequest error ", error);
  }
};

const debouncedSendRequest = debounce(
  async (updated: IFormDropdown[]) => await sendRequest(updated),
  3000
);

const FormDropdownsList: React.FC<IProps> = (props) => {
  const [search, setSearch] = useState("");
  const [formDropdowns, setFormDropdowns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedFormDropdown, setSelectedFormDropdown] = useState<any>({});

  useEffect(() => {
    const inputs = props.formDropdown || [];
    setFormDropdowns(inputs);
  }, [props.formDropdown]);

  function showEditDialog(id: any): void {
    const formDropdown = props.formDropdown.find((item: any) => item.id === id);
    setSelectedFormDropdown(formDropdown);
    setShowModal(true);
  }

  function handleSearchChanged(event: any): void {
    setSearch(event.target.value);
    const searchValue = event.target.value.toLowerCase();
    const filteredList = props.formDropdown?.filter((item: IFormDropdown) => {
      return item.option_text.toLowerCase().includes(searchValue);
    });
    setFormDropdowns(filteredList);
  }

  function handleOnSortEnd(oldIndex: number, newIndex: number): void {
    if (search.length > 0) return;
    const sorted = arrayMoveImmutable(formDropdowns, oldIndex, newIndex);
    const updatedItems = updateDisplayOrder(sorted);
    setFormDropdowns(updatedItems);
    debouncedSendRequest(updatedItems);
  }

  function handleOnSaved(formDropdown: IFormDropdown): void {
    const newFormDropdowns = formDropdowns.map((item: any) => {
      if (item.id === formDropdown.id) {
        return formDropdown;
      }
      return item;
    });
    props.onListUpdated(newFormDropdowns);
  }

  return (
    <>
      <FormDropdownsCreateDialog
        formDropdown={selectedFormDropdown}
        type={selectedFormDropdown?.dropdown_name}
        show={showModal}
        onHide={() => setShowModal(false)}
        onSaved={handleOnSaved}
      />
      <div className="p-3">
        <div className="row my-3 ml-3" style={{ maxWidth: 400 }}>
          <TextField
            id="filled-search"
            label="Search"
            type="search"
            variant="outlined"
            onChange={handleSearchChanged}
            value={search}
          />
        </div>
        <div className="row mt-3">
          <SortableList
            onSortEnd={handleOnSortEnd}
            draggedItemClassName="item-dragged"
            style={{ width: 620 }}
          >
            {formDropdowns.map((item: IFormDropdown) => (
              <SortableItem key={item.id}>
                <Paper
                  elevation={3}
                  key={item.id}
                  className="p-2 my-1 ml-3 sort-item"
                  style={{ width: 600 }}
                >
                  <div className="d-flex">
                    <SortableKnob>
                      <div>
                        <RiDraggable />
                      </div>
                    </SortableKnob>
                    <div className="flex-grow-1 fw-bold mt-1 ml-3">
                      <div className={item.is_active ? "" : "text-black-50"}>
                        {item.option_text}
                      </div>
                    </div>
                    <div>
                      <span
                        className={`badge rounded-pill ${
                          item.is_active == false
                            ? "text-bg-danger"
                            : "text-bg-success"
                        }`}
                      >
                        {item.is_active == false ? "Inactive" : "Active"}
                      </span>
                    </div>
                    <div className="sort-item-actions">
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm mr-2"
                          title="edit template"
                          onClick={() => showEditDialog(item.id)}
                        >
                          <BsPencilFill />
                        </button>
                      </div>
                    </div>
                  </div>
                </Paper>
              </SortableItem>
            ))}
          </SortableList>
        </div>
      </div>
    </>
  );
};

export default FormDropdownsList;
